/**
 * Normalize property API payloads into the view-model shape used by Property/Location views.
 */

/** True when the API/bundle carries a published property tree (not null or an empty shell). */
export function hasPublishedPropertyContent(property) {
  if (!property || typeof property !== 'object') return false;
  const name = String(property.name || '').trim();
  const siteName = String(property.siteName || '').trim();
  const description = String(property.description || '').trim();
  const marketing = property.marketing;
  if (marketing && typeof marketing === 'object') {
    if (String(marketing.description || '').trim()) return true;
    if (Array.isArray(marketing.gallery) && marketing.gallery.length > 0) return true;
    if (String(marketing.heroImage || '').trim()) return true;
  }
  return Boolean(name || siteName || description);
}

function buildLocationFromProperty(p) {
  return {
    address: p?.address || '',
    coordinates: p?.coordinates || { lat: 0, lng: 0 }
  };
}

function flatPropertyFromWire(p) {
  const marketing = p?.marketing || {};
  if (marketing.description) {
    return {
      siteName: p.siteName || '',
      siteSlogan: p.siteSlogan || '',
      name: p.name || '',
      address: p.address || '',
      coordinates: p.coordinates || { lat: 0, lng: 0 },
      specifications: p.specifications || {},
      description: marketing.description || '',
      features: marketing.features || [],
      heroImage: marketing.heroImage || '',
      gallery: marketing.gallery || [],
      manager: p.manager || null,
      publicPlanSiteFloorId: p.publicPlanSiteFloorId || null
    };
  }
  return {
    siteName: p.siteName || '',
    siteSlogan: p.siteSlogan || '',
    name: p.name || '',
    address: p.address || '',
    coordinates: p.coordinates || { lat: 0, lng: 0 },
    specifications: p.specifications || {},
    description: p.description || '',
    features: p.features || [],
    heroImage: p.heroImage || '',
    gallery: p.gallery || [],
    manager: p.manager || null,
    publicPlanSiteFloorId: p.publicPlanSiteFloorId || null
  };
}

export function normalizeFromPropertyApi(data) {
  if (!data?.success || !hasPublishedPropertyContent(data.property)) return null;
  const flat = flatPropertyFromWire(data.property);
  return {
    property: flat,
    floors: Array.isArray(data.floors) ? data.floors : [],
    location: buildLocationFromProperty(flat)
  };
}

export function normalizeFromBundledProperty({ property, floors } = {}) {
  if (!hasPublishedPropertyContent(property)) return null;
  const flat = flatPropertyFromWire(property);
  return {
    property: flat,
    floors: Array.isArray(floors) ? floors : [],
    location: buildLocationFromProperty(flat)
  };
}

export function normalizePropertyWire(input) {
  if (!input) return null;
  if (input.success !== undefined) {
    return normalizeFromPropertyApi(input);
  }
  if (input.property?.marketing) {
    return normalizeFromPropertyApi({
      success: true,
      property: input.property,
      floors: input.floors
    });
  }
  if (input.property) {
    return normalizeFromBundledProperty(input);
  }
  return null;
}
